import React, { useState, useEffect } from "react";
import { Box, Typography, LinearProgress, Tooltip, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { getSalesByProducts, getUserDetails } from "../../../Services/authService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SuccessNotification from "../../../Components/Common/SuccessNotification";

const ProductsChart = ({ selectedLocations, dateRange, applyClicked }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const chartColor2 = mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const borderColor1 = mode === "dark" ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
  const settingIconColor = mode === "dark" ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxSales, setMaxSales] = useState(0);
  const [showNoDataNotification, setShowNoDataNotification] = useState(false);
    const [franchiseId, setFranchiseId] = useState(null);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getUserDetails();

        // console.log("✅ User details:", response);

        setFranchiseId(response?.franchiseId);
      } catch (error) {
        console.error("❌ Failed to fetch user:", error);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setError(null); // Reset error on each fetch
      setLoading(true);

      try {
        const formatLocalDate = (date) => {
          if (!date) return null;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const startDate = dateRange[0]?.startDate ? formatLocalDate(new Date(dateRange[0].startDate)) : null;
        const endDate = dateRange[0]?.endDate ? formatLocalDate(new Date(dateRange[0].endDate)) : null;

        const response = await getSalesByProducts(startDate, endDate, selectedLocations);

        if (response && response?.length > 0) {
          const transformedData = response.map((item) => ({
            name: item.productName,
            description: item.description,
            quantity: item.quantitySold,
            sales: item.totalRevenue,
          }));
          setData(transformedData);
          setMaxSales(response.length > 0 ? Math.max(...response.map((p) => p.quantitySold)) : 0)
          // console.log(response.length > 0 ? Math.max(...response.map((p) => p.quantitySold)) : 0)
        } else {
          setData([]); // Ensure empty state is handled properly
        }
      } catch (err) {
        setError("Failed to load sales data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedLocations, applyClicked, dateRange]); // Added dateRange dependency for accurate data refresh

  const handleDownloadExcel = () => {
    if (!data || data.length === 0) {
      setShowNoDataNotification(true);
      return;
    }

    const excelData = data.map((item, index) => ({
      [t("sales_analytics.sNo", "Nr.")]: index + 1,
      // [t("sales_analytics.productName", "Product Name")]: item.name,

      // Franchise 136 → Description
      // Other franchises → Product Name
      [Number(franchiseId) === 136 ? t("inventory.description", "Beschreibung") : t("sales_analytics.productName", "Produktname")]:
        Number(franchiseId) === 136 ? item.description : item.name,

      [t("sales_analytics.quantitySold", "Verkaufte Menge")]: item.quantity,
      [t("sales_analytics.totalRevenue", "Gesamtumsatz (€)")]: item.sales,
    }));
    // }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Top Selling Products `);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const timestamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
    saveAs(
      blob,
      `${t("sales_analytics.topSellingProductsFileName")}_${timestamp}.xlsx`
    );
  };

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: "2.5rem" }}>
          <Typography variant="h6" className="subheading-sec-card">
            {t("sales_analytics.TopSellingProducts")}
          </Typography>

          <Button id='export-btn' className='export-sales-btn mem-export-sales-btn' style={{ fontSize: "13px", background: "transparent", color: settingIconColor, border: `0.5px solid ${borderColor1}`, padding: "9.5px 26px" }} onClick={handleDownloadExcel} endIcon={<FileUploadOutlinedIcon />}>
            {t('sales_analytics.export')}
          </Button>
        </Box>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box sx={{ maxHeight: 400, overflowY: "auto", paddingRight: "2rem" }}>
            {data.length > 0 ? (
              data.map((product, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  mb={1.5}
                >
                  <Box
                    id="top_products_sec"
                    key={index}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    // gap={2}
                    mb={1.5}
                    sx={{ width: "40%", }}

                  >

                    <Tooltip title={product.name} placement="top" arrow>
                      <Typography
                        className="popup-text"
                        id="top_products_text1"

                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "default" }}
                      >
                        {product.name}
                      </Typography>
                    </Tooltip>
                    <Typography
                      className="popup-text"
                      id="top_products_text"

                      sx={{ whiteSpace: "nowrap", minWidth: "30px" }}
                    >
                      ({product.quantity})
                    </Typography>

                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={maxSales > 0 ? (product.quantity / maxSales) * 100 : 0}
                      sx={{
                        height: 8,
                        borderRadius: 5,
                        bgcolor: "transparent",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: chartColor2,
                          borderRadius: 5,
                        },
                      }}
                    />
                  </Box>

                  <Typography className="popup-text1">€ {product.sales}</Typography>
                </Box>
              ))
            ) : (
              <Typography>No sales data available.</Typography>
            )}
          </Box>
        )}
      </Box>
      <SuccessNotification
        open={showNoDataNotification}
        message={t('discounts.Nodataexport')}
        severity="warning"
        onClose={() => setShowNoDataNotification(false)}
      />
    </>
  );
};

export default ProductsChart;